import { Injectable } from '@angular/core';

export interface AdministrativeArea {
  name: string;
  abbreviation: string;
}

export interface CountryAdministrativeAreaConfig {
  label: string;
  postalCodeLabel: string;
  areas?: AdministrativeArea[];
}

@Injectable({
  providedIn: 'root'
})
export class InternationalAddressService {

  /**
   * Map of country codes to their administrative area configurations
   */
  private countryConfigs: { [key: string]: CountryAdministrativeAreaConfig } = {
    'US': {
      label: 'State',
      postalCodeLabel: 'ZIP Code',
      areas: [
        { name: "Alabama", abbreviation: "AL" },
        { name: "Alaska", abbreviation: "AK" },
        { name: "Arizona", abbreviation: "AZ" },
        { name: "Arkansas", abbreviation: "AR" },
        { name: "California", abbreviation: "CA" },
        { name: "Colorado", abbreviation: "CO" },
        { name: "Connecticut", abbreviation: "CT" },
        { name: "Delaware", abbreviation: "DE" },
        { name: "Florida", abbreviation: "FL" },
        { name: "Georgia", abbreviation: "GA" },
        { name: "Hawaii", abbreviation: "HI" },
        { name: "Idaho", abbreviation: "ID" },
        { name: "Illinois", abbreviation: "IL" },
        { name: "Indiana", abbreviation: "IN" },
        { name: "Iowa", abbreviation: "IA" },
        { name: "Kansas", abbreviation: "KS" },
        { name: "Kentucky", abbreviation: "KY" },
        { name: "Louisiana", abbreviation: "LA" },
        { name: "Maine", abbreviation: "ME" },
        { name: "Maryland", abbreviation: "MD" },
        { name: "Massachusetts", abbreviation: "MA" },
        { name: "Michigan", abbreviation: "MI" },
        { name: "Minnesota", abbreviation: "MN" },
        { name: "Mississippi", abbreviation: "MS" },
        { name: "Missouri", abbreviation: "MO" },
        { name: "Montana", abbreviation: "MT" },
        { name: "Nebraska", abbreviation: "NE" },
        { name: "Nevada", abbreviation: "NV" },
        { name: "New Hampshire", abbreviation: "NH" },
        { name: "New Jersey", abbreviation: "NJ" },
        { name: "New Mexico", abbreviation: "NM" },
        { name: "New York", abbreviation: "NY" },
        { name: "North Carolina", abbreviation: "NC" },
        { name: "North Dakota", abbreviation: "ND" },
        { name: "Ohio", abbreviation: "OH" },
        { name: "Oklahoma", abbreviation: "OK" },
        { name: "Oregon", abbreviation: "OR" },
        { name: "Pennsylvania", abbreviation: "PA" },
        { name: "Rhode Island", abbreviation: "RI" },
        { name: "South Carolina", abbreviation: "SC" },
        { name: "South Dakota", abbreviation: "SD" },
        { name: "Tennessee", abbreviation: "TN" },
        { name: "Texas", abbreviation: "TX" },
        { name: "Utah", abbreviation: "UT" },
        { name: "Vermont", abbreviation: "VT" },
        { name: "Virginia", abbreviation: "VA" },
        { name: "Washington", abbreviation: "WA" },
        { name: "West Virginia", abbreviation: "WV" },
        { name: "Wisconsin", abbreviation: "WI" },
        { name: "Wyoming", abbreviation: "WY" }
      ]
    },
    'CA': {
      label: 'Province',
      postalCodeLabel: 'Postal Code',
      areas: [
        { name: "Alberta", abbreviation: "AB" },
        { name: "British Columbia", abbreviation: "BC" },
        { name: "Manitoba", abbreviation: "MB" },
        { name: "New Brunswick", abbreviation: "NB" },
        { name: "Newfoundland and Labrador", abbreviation: "NL" },
        { name: "Northwest Territories", abbreviation: "NT" },
        { name: "Nova Scotia", abbreviation: "NS" },
        { name: "Nunavut", abbreviation: "NU" },
        { name: "Ontario", abbreviation: "ON" },
        { name: "Prince Edward Island", abbreviation: "PE" },
        { name: "Quebec", abbreviation: "QC" },
        { name: "Saskatchewan", abbreviation: "SK" },
        { name: "Yukon", abbreviation: "YT" }
      ]
    },
    'AU': {
      label: 'State/Territory',
      postalCodeLabel: 'Postcode',
      areas: [
        { name: "Australian Capital Territory", abbreviation: "ACT" },
        { name: "New South Wales", abbreviation: "NSW" },
        { name: "Northern Territory", abbreviation: "NT" },
        { name: "Queensland", abbreviation: "QLD" },
        { name: "South Australia", abbreviation: "SA" },
        { name: "Tasmania", abbreviation: "TAS" },
        { name: "Victoria", abbreviation: "VIC" },
        { name: "Western Australia", abbreviation: "WA" }
      ]
    },
    'GB': {
      label: 'Region',
      postalCodeLabel: 'Postcode',
      areas: [
        { name: "England", abbreviation: "ENG" },
        { name: "Northern Ireland", abbreviation: "NIR" },
        { name: "Scotland", abbreviation: "SCT" },
        { name: "Wales", abbreviation: "WLS" }
      ]
    },
    'IT': {
      label: 'Province',
      postalCodeLabel: 'CAP',
      areas: [
        { name: "Agrigento", abbreviation: "AG" },
        { name: "Alessandria", abbreviation: "AL" },
        { name: "Ancona", abbreviation: "AN" },
        { name: "Aosta", abbreviation: "AO" },
        { name: "Arezzo", abbreviation: "AR" },
        { name: "Ascoli Piceno", abbreviation: "AP" },
        { name: "Asti", abbreviation: "AT" },
        { name: "Avellino", abbreviation: "AV" },
        { name: "Bari", abbreviation: "BA" },
        { name: "Barletta-Andria-Trani", abbreviation: "BT" },
        { name: "Belluno", abbreviation: "BL" },
        { name: "Benevento", abbreviation: "BN" },
        { name: "Bergamo", abbreviation: "BG" },
        { name: "Biella", abbreviation: "BI" },
        { name: "Bologna", abbreviation: "BO" },
        { name: "Bolzano", abbreviation: "BZ" },
        { name: "Brescia", abbreviation: "BS" },
        { name: "Brindisi", abbreviation: "BR" },
        { name: "Cagliari", abbreviation: "CA" },
        { name: "Caltanissetta", abbreviation: "CL" },
        { name: "Campobasso", abbreviation: "CB" },
        { name: "Carbonia-Iglesias", abbreviation: "CI" },
        { name: "Caserta", abbreviation: "CE" },
        { name: "Catania", abbreviation: "CT" },
        { name: "Catanzaro", abbreviation: "CZ" },
        { name: "Chieti", abbreviation: "CH" },
        { name: "Como", abbreviation: "CO" },
        { name: "Cosenza", abbreviation: "CS" },
        { name: "Cremona", abbreviation: "CR" },
        { name: "Crotone", abbreviation: "KR" },
        { name: "Cuneo", abbreviation: "CN" },
        { name: "Enna", abbreviation: "EN" },
        { name: "Fermo", abbreviation: "FM" },
        { name: "Ferrara", abbreviation: "FE" },
        { name: "Firenze", abbreviation: "FI" },
        { name: "Foggia", abbreviation: "FG" },
        { name: "Forlì-Cesena", abbreviation: "FC" },
        { name: "Frosinone", abbreviation: "FR" },
        { name: "Genova", abbreviation: "GE" },
        { name: "Gorizia", abbreviation: "GO" },
        { name: "Grosseto", abbreviation: "GR" },
        { name: "Imperia", abbreviation: "IM" },
        { name: "Isernia", abbreviation: "IS" },
        { name: "L'Aquila", abbreviation: "AQ" },
        { name: "La Spezia", abbreviation: "SP" },
        { name: "Latina", abbreviation: "LT" },
        { name: "Lecce", abbreviation: "LE" },
        { name: "Lecco", abbreviation: "LC" },
        { name: "Livorno", abbreviation: "LI" },
        { name: "Lodi", abbreviation: "LO" },
        { name: "Lucca", abbreviation: "LU" },
        { name: "Macerata", abbreviation: "MC" },
        { name: "Mantova", abbreviation: "MN" },
        { name: "Massa-Carrara", abbreviation: "MS" },
        { name: "Matera", abbreviation: "MT" },
        { name: "Medio Campidano", abbreviation: "VS" },
        { name: "Messina", abbreviation: "ME" },
        { name: "Milano", abbreviation: "MI" },
        { name: "Modena", abbreviation: "MO" },
        { name: "Monza e Brianza", abbreviation: "MB" },
        { name: "Napoli", abbreviation: "NA" },
        { name: "Novara", abbreviation: "NO" },
        { name: "Nuoro", abbreviation: "NU" },
        { name: "Ogliastra", abbreviation: "OG" },
        { name: "Olbia-Tempio", abbreviation: "OT" },
        { name: "Oristano", abbreviation: "OR" },
        { name: "Padova", abbreviation: "PD" },
        { name: "Palermo", abbreviation: "PA" },
        { name: "Parma", abbreviation: "PR" },
        { name: "Pavia", abbreviation: "PV" },
        { name: "Perugia", abbreviation: "PG" },
        { name: "Pesaro e Urbino", abbreviation: "PU" },
        { name: "Pescara", abbreviation: "PE" },
        { name: "Piacenza", abbreviation: "PC" },
        { name: "Pisa", abbreviation: "PI" },
        { name: "Pistoia", abbreviation: "PT" },
        { name: "Pordenone", abbreviation: "PN" },
        { name: "Potenza", abbreviation: "PZ" },
        { name: "Prato", abbreviation: "PO" },
        { name: "Ragusa", abbreviation: "RG" },
        { name: "Ravenna", abbreviation: "RA" },
        { name: "Reggio Calabria", abbreviation: "RC" },
        { name: "Reggio Emilia", abbreviation: "RE" },
        { name: "Rieti", abbreviation: "RI" },
        { name: "Rimini", abbreviation: "RN" },
        { name: "Roma", abbreviation: "RM" },
        { name: "Rovigo", abbreviation: "RO" },
        { name: "Salerno", abbreviation: "SA" },
        { name: "Sassari", abbreviation: "SS" },
        { name: "Savona", abbreviation: "SV" },
        { name: "Siena", abbreviation: "SI" },
        { name: "Siracusa", abbreviation: "SR" },
        { name: "Sondrio", abbreviation: "SO" },
        { name: "Taranto", abbreviation: "TA" },
        { name: "Teramo", abbreviation: "TE" },
        { name: "Terni", abbreviation: "TR" },
        { name: "Torino", abbreviation: "TO" },
        { name: "Trapani", abbreviation: "TP" },
        { name: "Trento", abbreviation: "TN" },
        { name: "Treviso", abbreviation: "TV" },
        { name: "Trieste", abbreviation: "TS" },
        { name: "Udine", abbreviation: "UD" },
        { name: "Varese", abbreviation: "VA" },
        { name: "Venezia", abbreviation: "VE" },
        { name: "Verbano-Cusio-Ossola", abbreviation: "VB" },
        { name: "Vercelli", abbreviation: "VC" },
        { name: "Verona", abbreviation: "VR" },
        { name: "Vibo Valentia", abbreviation: "VV" },
        { name: "Vicenza", abbreviation: "VI" },
        { name: "Viterbo", abbreviation: "VT" }
      ]
    },
    'MX': {
      label: 'State',
      postalCodeLabel: 'Postal Code',
      areas: [
        { name: "Aguascalientes", abbreviation: "AGU" },
        { name: "Baja California", abbreviation: "BCN" },
        { name: "Baja California Sur", abbreviation: "BCS" },
        { name: "Campeche", abbreviation: "CAM" },
        { name: "Chiapas", abbreviation: "CHP" },
        { name: "Chihuahua", abbreviation: "CHH" },
        { name: "Coahuila", abbreviation: "COA" },
        { name: "Colima", abbreviation: "COL" },
        { name: "Durango", abbreviation: "DUR" },
        { name: "Guanajuato", abbreviation: "GUA" },
        { name: "Guerrero", abbreviation: "GRO" },
        { name: "Hidalgo", abbreviation: "HID" },
        { name: "Jalisco", abbreviation: "JAL" },
        { name: "México", abbreviation: "MEX" },
        { name: "Michoacán", abbreviation: "MIC" },
        { name: "Morelos", abbreviation: "MOR" },
        { name: "Nayarit", abbreviation: "NAY" },
        { name: "Nuevo León", abbreviation: "NLE" },
        { name: "Oaxaca", abbreviation: "OAX" },
        { name: "Puebla", abbreviation: "PUE" },
        { name: "Querétaro", abbreviation: "QUE" },
        { name: "Quintana Roo", abbreviation: "ROO" },
        { name: "San Luis Potosí", abbreviation: "SLP" },
        { name: "Sinaloa", abbreviation: "SIN" },
        { name: "Sonora", abbreviation: "SON" },
        { name: "Tabasco", abbreviation: "TAB" },
        { name: "Tamaulipas", abbreviation: "TAM" },
        { name: "Tlaxcala", abbreviation: "TLA" },
        { name: "Veracruz", abbreviation: "VER" },
        { name: "Yucatán", abbreviation: "YUC" },
        { name: "Zacatecas", abbreviation: "ZAC" }
      ]
    },
    'FR': {
      label: 'Region',
      postalCodeLabel: 'Code Postal',
      areas: []
    },
    'DE': {
      label: 'State',
      postalCodeLabel: 'PLZ',
      areas: []
    },
    'JP': {
      label: 'Prefecture',
      postalCodeLabel: 'Postal Code',
      areas: []
    },
    'CN': {
      label: 'Province',
      postalCodeLabel: 'Postal Code',
      areas: []
    }
  };

  constructor() { }

  /**
   * Get the administrative area label for a country
   */
  getAdministrativeAreaLabel(countryCode: string): string {
    return this.countryConfigs[countryCode]?.label || 'State/Province';
  }

  /**
   * Get the postal code label for a country
   */
  getPostalCodeLabel(countryCode: string): string {
    return this.countryConfigs[countryCode]?.postalCodeLabel || 'Postal/ZIP Code';
  }

  /**
   * Get administrative areas for a country
   */
  getAdministrativeAreas(countryCode: string): AdministrativeArea[] {
    return this.countryConfigs[countryCode]?.areas || [];
  }

  /**
   * Check if a country has predefined administrative areas
   */
  hasAdministrativeAreas(countryCode: string): boolean {
    const areas = this.getAdministrativeAreas(countryCode);
    return areas && areas.length > 0;
  }

  /**
   * Get the full country configuration
   */
  getCountryConfig(countryCode: string): CountryAdministrativeAreaConfig | null {
    return this.countryConfigs[countryCode] || null;
  }
}

